import { useMemo } from "react";
import {
  Box,
  CssBaseline,
  Stack,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavBarMode, useNavBarTitle } from "components/NavBar";
import { type DatasetKind } from "../api";
import t from "../translations";
import { DatasetDetailProvider, useDatasetDetail } from "./DatasetDetailContext";
import DatasetImageList from "./DatasetImageList";
import DatasetImagePanel from "./DatasetImagePanel";
import CroppedImages from "./CroppedImages";

interface DatasetDetailProps {
  kind?: DatasetKind;
}

export default function DatasetDetail({
  kind = "object",
}: DatasetDetailProps = {}) {
  const params = useParams<{ datasetId: string; imageId: string }>();
  const datasetId = params.datasetId ? parseInt(params.datasetId) : 0;
  const imageId = params.imageId ? parseInt(params.imageId) : 0;

  const darkTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: "dark",
          background: { default: "#1e1e1e", paper: "#2d2d2d" },
        },
      }),
    []
  );

  useNavBarMode("dark");

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline enableColorScheme />
      <Box
        sx={{
          height: "100%",
          width: "100%",
          bgcolor: "background.default",
          color: "text.primary",
        }}
      >
        <DatasetDetailProvider
          datasetId={datasetId}
          imageId={imageId}
          kind={kind}
        >
          <DatasetDetailContent imageSelected={Boolean(params.imageId)} />
        </DatasetDetailProvider>
      </Box>
    </ThemeProvider>
  );
}

function DatasetDetailContent({ imageSelected }: { imageSelected: boolean }) {
  const { datasetName } = useDatasetDetail();

  useNavBarTitle(datasetName ? `Dataset: ${datasetName}` : t.PageTitle);

  return (
    <Stack
      sx={{
        flexDirection: "row",
        height: "100%",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <DatasetImageList />
      {imageSelected && <CroppedImages />}
      {imageSelected && <DatasetImagePanel />}
    </Stack>
  );
}
